import * as React from 'react';
import { LocationGeneralCardComponent } from 'components/location/cards/LocationGeneralCardComponent';
import { LocationGeneralEditCardComponent } from 'components/location/cards/LocationGeneralEditCardComponent';
import { createLocationGeneralForm } from 'components/location/forms/createLocationGeneralForm';
import { Location } from 'components/location/models/LocationModel';
import { LegacyForm } from 'lib/LegacyForm';
import { Panel } from 'ui/Panel';
import { locationApi } from '../LocationApi';
import { useSelector } from 'react-redux';
import { ApplicationState } from 'store/store';
import { Permission, Resource } from '@pepperhq/auth-client';

export interface LocationGeneralCardProps {
    currentLocation: Location;
    setCurrentLocation: (location: Location) => void;
}

export const LocationGeneralCard: React.FC<LocationGeneralCardProps> = ({
    currentLocation,
    setCurrentLocation
}) => {
    const { claims } = useSelector((state: ApplicationState) => state.auth);
    const form = React.useRef<LegacyForm>(null);
    const [isEditing, setIsEditing] = React.useState(false);
    const updateForm = React.useCallback((data: Location) => {
        const { title, state, contacts, description, VATnumber, buttons, timezone } = data;
        const { phone, email } = contacts || { phone: '', email: '' };
        form.current.update({
            title,
            state,
            phone,
            email,
            description,
            VATnumber,
            buttons,
            timezone: { label: timezone, value: timezone }
        });
    }, []);
    const handleSubmit = React.useCallback(
        async (data: any) => {
            const locationId = currentLocation?._id;
            const { email, phone, timezone, ...rest } = data;
            const response = await locationApi.update(locationId, {
                body: {
                    ...rest,
                    contacts: {
                        email,
                        phone
                    },
                    timezone: timezone?.value
                }
            });
            if (response.ok) {
                updateForm(response.body);
                setCurrentLocation(response.body);
                setIsEditing(false);
            }
        },
        [currentLocation?._id, setCurrentLocation, updateForm]
    );
    React.useEffect(() => {
        form.current = new LegacyForm(
            createLocationGeneralForm(claims.hasPermission(Resource.Location, Permission.all)),
            handleSubmit
        );
    }, [claims, handleSubmit]);
    React.useCallback(() => {
        updateForm(currentLocation);
    }, [currentLocation, updateForm]);
    const toggleState = React.useCallback(() => {
        setIsEditing(currentValue => !currentValue);
        updateForm(currentLocation);
    }, [currentLocation, updateForm]);
    return (
        <Panel title="General" showAction={!isEditing} onClick={toggleState} divider>
            {isEditing ? (
                <LocationGeneralEditCardComponent
                    onClose={toggleState}
                    form={form.current}
                    location={currentLocation}
                />
            ) : (
                <LocationGeneralCardComponent location={currentLocation} />
            )}
        </Panel>
    );
};
